import React, {useContext, useEffect} from 'react';
import {BrowserRouter as Router, Route, Routes, useLocation, useNavigate} from 'react-router-dom';
import PictureDetail from 'components/pictures/detail/PictureDetail';
import WrappedUploadPage from './components/pictures/UploadPage';
import UploadImageButton from './components/menu/UploadImageButton';
import ProfileEdit from 'components/pictures/list/ProfileEdit';
import LeftMenuBar from './components/menu/LeftMenuBar';
import Lootboxes from 'components/lootbox/Lootboxes';
import './App.css';
import ListItem from './components/items/ListItem';
import Transactions from './components/settings/Transactions';
import ChatPanel from './components/chat/ChatPanel';
import Swag from './components/settings/Swag';
import ShareImage from 'components/pictures/share/ShareImage';
import {PopupProvider, usePopup} from 'popup/PopupContext';
import {UserContext, UserProvider} from 'components/user/UserContext';
import {ImageCacheProvider} from 'components/cacheable/ImageCacheContext';
import ItemDetail from 'components/items/detail/ItemDetail';
import PicturesPage from 'components/pictures/list/PicturesPage';
import ProfilePage from 'components/pictures/list/ProfilePage';
import {MarketStateProvider} from 'util/market/MarketContext';
import SignInPopup from 'components/login/SignInPopup';
import {WebSocketProvider} from 'wss/WebSocketService';
import {getTitleForLocation, singlePageLocation} from 'util/locationUtil';
import {PicturesStateProvider} from 'util/image/PictureContext';
import {NotificationProvider} from 'util/NotificationContext';
import {SessionConfigurationProvider, useSessionConfiguration} from 'util/SessionConfigurationContext';
import ReorderWrapper from 'util/ReorderWrapper';
import TransactionDetails from 'components/settings/TransactionDetails';
import RightPanel from 'components/menu/RightPanel';
import {CommentStateProvider} from 'util/CommentContext';
import NotificationPane from 'components/notification/NotificationPane';
import BottomMenuBar from 'components/menu/mobile/BottomMenuBar';
import MobileProfile from 'components/menu/mobile/MobileProfile';
import MobileNotifications from 'components/menu/mobile/MobileNotifications';
import {Tooltip} from 'react-tooltip';
import {Helmet} from 'react-helmet';
import {isMoneyHostname} from 'util/configuration';
import DonationList from 'components/donations/DonationList';
import AdminConsole from 'components/admin/BasicAdminConsole';
import PageRules from "./components/donations/RulesPage";
import NotFoundPage from "./components/menu/NotFoundPage";
import TransactionFailedPage from "./components/menu/TransactionFailedPage";

const AppContent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {user} = useContext(UserContext);
    const {openPopup} = usePopup();
    const {isDesktopView} = useSessionConfiguration();
    const isOnImagesPage = () => {
        const locationName = singlePageLocation(location.pathname);
        const addImagePaths = ['top', 'trending', 'image', 'profile'];
        return addImagePaths.includes(locationName);
    };
    const isMoney = isMoneyHostname();

    useEffect(() => {
        if (!user.isLoggedIn) {
            const locationName = singlePageLocation(location.pathname);
            const isOnAllowedLocation =
                locationName === 'top' ||
                locationName === 'trending' ||
                locationName === 'profile' ||
                locationName === 'image' ||
                locationName === 'verify-email' ||
                locationName === 'page-rules';
            if (!isOnAllowedLocation) {
                navigate('/', {replace: true});
                openPopup(
                    <SignInPopup/>,
                    () => {
                    },
                    false,
                    true
                );
            }
        }
    }, [user.isLoggedIn, location.pathname, navigate]);

    // Use the utility function to set the document title based on the current location
    useEffect(() => {
        document.title = getTitleForLocation(location.pathname, isMoney);
    }, [location.pathname]);

    function isOnBigSpace() {
        const locationName = singlePageLocation(location.pathname);
        return locationName === 'profile' || locationName === 'mobile-menu';
    }

    return (
        <>
            <div className="position-wrapper">
                {isDesktopView ? (<LeftMenuBar/>) : (<BottomMenuBar/>)}
                <div className="main-content-wrapper">
                    <div
                        className={`main-content ${isDesktopView ? '' : 'mobile'} ${isOnBigSpace() ? 'big-space' : ''}`}>
                        <Tooltip id="main-tooltip" place="top" className="tooltip-style" border="1px solid gray"/>

                        <Routes>
                            <Route path="/" element={<PicturesPage component={'TOP'}/>}/>
                            <Route path="/trending" element={<PicturesPage component={'TRENDING'}/>}/>
                            <Route path="/profile/:userId" element={<ProfilePage/>}/>
                            <Route path="/image/:browsing/:id" element={<PictureDetail/>}/>
                            <Route path="/image/share/:imageId" element={<ShareImage/>}/>
                            <Route path="/upload" element={<WrappedUploadPage/>}/>
                            <Route path="/success" element={<div>Payment Successful!</div>}/>
                            <Route path="/cancel" element={<div>Payment Cancelled.</div>}/>
                            <Route path="/profile/edit" element={<ProfileEdit/>}/>
                            <Route path="/lootbox" element={<Lootboxes/>}/>
                            <Route path="/my-items" element={<ListItem type="my-items"/>}/>
                            <Route path="/item-store" element={<ListItem type="item-store"/>}/>
                            <Route path="/items-detail/:item/:type" element={<ItemDetail/>}/>
                            <Route path="/history" element={<Transactions/>}/>
                            <Route path="/history/:type" element={<TransactionDetails/>}/>
                            <Route path="/chat" element={<ChatPanel/>}/>
                            <Route path="/swag" element={<Swag/>}/>
                            <Route path="/page-rules" element={<PageRules/>}/>
                            <Route path="/notifications" element={<NotificationPane/>}/>
                            <Route path="/mobile-profile" element={<MobileProfile/>}/>
                            <Route path="/mobile-notifications" element={<MobileNotifications/>}/>
                            {!isMoney && (
                                <>
                                    <Route path="/my-donations" element={<DonationList myDonations={true}/>}/>
                                    <Route path="/public-donations"
                                           element={<DonationList myDonations={false}/>}/>
                                </>
                            )}
                            <Route path="/admin-console" element={<AdminConsole/>}/>
                            {isMoney && (
                                <Route path="/transaction-failed" element={<TransactionFailedPage/>}/>
                            )}
                            <Route path="*" element={<NotFoundPage/>}/>
                        </Routes>
                    </div>
                </div>
                {isDesktopView && <RightPanel/>}
            </div>
            {isDesktopView && isOnImagesPage() && <UploadImageButton/>}
        </>
    );
};

const App = () => {
    const getHelmet = () => {
        const isMoney = isMoneyHostname();
        console.log("Helmet - isMoney: ", isMoney)
        if (isMoney) {
            return (
                <Helmet>
                    <title>Flextagram - The ultimate flex</title>
                    <meta name="description" content="Social network with expensive twist."/>
                    <link id="app-favicon" rel="icon" type="image/svg+xml"
                          href="/images/logo/logo-coiny-header-muscle.svg"/>
                </Helmet>);
        } else {
            return (
                <Helmet>
                    <title>Flexagren - The green app</title>
                    <meta name="description" content="Social network with green money."/>
                    <link id="app-favicon" rel="icon" type="image/svg+xml"
                          href="/images/logo/logo-coiny-header-hearth.svg"/>
                </Helmet>);
        }
    };

    return (
        <div>
            {getHelmet()}
            <WebSocketProvider>
                <Router>
                    <UserProvider>
                        <NotificationProvider>
                            <ImageCacheProvider>
                                <PicturesStateProvider>
                                    <MarketStateProvider>
                                        <CommentStateProvider>
                                            <SessionConfigurationProvider>
                                                <div className="App">
                                                    <PopupProvider>
                                                        <ReorderWrapper>
                                                            <AppContent/>
                                                        </ReorderWrapper>
                                                    </PopupProvider>
                                                </div>
                                            </SessionConfigurationProvider>
                                        </CommentStateProvider>
                                    </MarketStateProvider>
                                </PicturesStateProvider>
                            </ImageCacheProvider>
                        </NotificationProvider>
                    </UserProvider>
                </Router>
            </WebSocketProvider>
        </div>
    );
};

export default App;
